import React from 'react';
import { Link } from 'react-router-dom';

const YEAR = new Date().getFullYear();

const TERMS = {
  title: 'Terms of Use',
  updated: 'July 21, 2026',
  sections: [
    {
      heading: 'Agreement',
      body: `By accessing or using Pocket Parliamentarian (“the Service”), you agree to these Terms of Use. If you do not agree, do not use the Service.`,
    },
    {
      heading: 'The Service',
      body: `Pocket Parliamentarian provides software to help organizations run meetings under common parliamentary procedure, record minutes, and manage related meeting tools. Features may change as the product develops. Some capabilities described in marketing materials may be limited, gated, or not yet available.`,
    },
    {
      heading: 'Accounts and organizations',
      body: `You are responsible for the accuracy of information you provide, for safeguarding login credentials, and for activity under your account. Organization administrators control memberships and access within their organization. You must have authority to invite others and to process their contact information.`,
    },
    {
      heading: 'Acceptable use',
      body: `You may not misuse the Service, attempt to gain unauthorized access, disrupt other users, scrape or reverse-engineer the Service for competing products in violation of law, or use the Service for unlawful purposes. We may suspend or terminate access for abuse or security risk.`,
    },
    {
      heading: 'Intellectual property',
      body: `The Service, branding, and underlying software are owned by Pocket Parliamentarian and its licensors. These Terms do not transfer ownership to you. You retain rights to content you create (such as meeting minutes); you grant us a limited license to host and process that content solely to operate the Service.`,
    },
    {
      heading: 'No legal advice',
      body: `The Service assists with meeting procedure and records. It does not constitute legal advice. You remain responsible for compliance with your bylaws, applicable law, and any official record-keeping obligations.`,
    },
    {
      heading: 'Disclaimers and liability',
      body: `THE SERVICE IS PROVIDED “AS IS” WITHOUT WARRANTIES OF ANY KIND TO THE FULLEST EXTENT PERMITTED BY LAW. TO THE MAXIMUM EXTENT PERMITTED, WE ARE NOT LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES, OR LOSS OF DATA, PROFITS, OR BUSINESS ARISING FROM USE OF THE SERVICE.`,
    },
    {
      heading: 'Changes',
      body: `We may update these Terms. Continued use after changes constitutes acceptance of the revised Terms. The “Last updated” date above reflects the latest revision.`,
    },
    {
      heading: 'Contact',
      body: `Questions about these Terms: hello@pocketparliamentarian.com`,
    },
  ],
};

const PRIVACY = {
  title: 'Privacy Policy',
  updated: 'July 21, 2026',
  sections: [
    {
      heading: 'Overview',
      body: `This Privacy Policy describes how Pocket Parliamentarian collects, uses, and shares information when you use our website and application.`,
    },
    {
      heading: 'Information we collect',
      body: `Account data (such as name and email), organization membership and role information, meeting-related content you enter (minutes, motions, settings), technical logs (IP address, device/browser type, timestamps), and communications you send to us.`,
    },
    {
      heading: 'How we use information',
      body: `We use information to operate and improve the Service, authenticate users, sync live meetings, store Meeting Log archives you create, send transactional email (invites, auth), provide support, and protect against abuse.`,
    },
    {
      heading: 'Sharing',
      body: `We use infrastructure providers (for example hosting and database services) to run the Service. They process data on our behalf under contractual obligations. We do not sell your personal information. We may disclose information if required by law or to protect rights, safety, or security.`,
    },
    {
      heading: 'Data retention',
      body: `We retain account and organization data while your account is active and as needed for backups, legal obligations, and dispute resolution. Organization administrators may remove members; deleting an organization or requesting account closure can be arranged by contacting us.`,
    },
    {
      heading: 'Security',
      body: `We use industry-standard measures such as encrypted transport (HTTPS), access controls, and database row-level security. No method of transmission or storage is completely secure.`,
    },
    {
      heading: 'Children',
      body: `The Service is not directed to children under 13, and we do not knowingly collect personal information from them.`,
    },
    {
      heading: 'Your choices',
      body: `You may update profile information in the app, leave an organization (subject to admin controls), and request access or deletion by contacting us. Marketing email, if any, will include an unsubscribe option.`,
    },
    {
      heading: 'Changes',
      body: `We may update this Policy. We will revise the “Last updated” date and, when appropriate, provide additional notice.`,
    },
    {
      heading: 'Contact',
      body: `Privacy questions: hello@pocketparliamentarian.com`,
    },
  ],
};

export default function LegalPage({ doc = 'terms' }) {
  const content = doc === 'privacy' ? PRIVACY : TERMS;

  return (
    <div className="min-h-screen bg-pp-paper text-pp-ink">
      <header className="border-b border-pp-line bg-pp-surface">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <Link to="/" className="text-sm font-semibold text-pp-accent hover:underline">
            Pocket Parliamentarian
          </Link>
          <Link to="/login" className="text-sm font-medium text-pp-muted hover:text-pp-ink">
            Log in
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
        <h1 className="text-3xl font-semibold tracking-tight">{content.title}</h1>
        <p className="mt-2 text-sm text-pp-muted">Last updated: {content.updated}</p>
        <div className="mt-8 space-y-8">
          {content.sections.map((section) => (
            <section key={section.heading}>
              <h2 className="text-lg font-semibold tracking-tight">{section.heading}</h2>
              <p className="mt-2 text-sm leading-relaxed text-pp-ink/90 whitespace-pre-wrap">
                {section.body}
              </p>
            </section>
          ))}
        </div>
        <p className="mt-12 text-xs text-pp-muted">
          © {YEAR} Pocket Parliamentarian. All rights reserved.
        </p>
      </main>
    </div>
  );
}
